import React, { useCallback, useEffect, useState } from 'react';

import useMedia from '../../../../common/hooks/useMedia';
import { searchOrigins } from '../../../Navigation/constants';
import {
  NavSearchContext,
  NavSearchContextProvider,
  NavSearchProps,
} from './NavSearchContext';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import NavSearchButton from './NavSearchElements/NavSearchButton';
import NavSearchDropdown from './NavSearchElements/NavSearchDropdown';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';

export default function NavSearch(props: NavSearchProps) {
  const {
    onSubmit,
    userHasAnySubscription,
    useSnapshots,
    searchOrigin: searchOriginFromProps = searchOrigins.TOP_NAV,
  } = props;
  const [contentType, setContentType] = useState(props.contentType);
  const [searchTerm, setSearchTerm] = useState('');

  const makeEmptySuggestions = () => ({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

  const clearSuggestions = useCallback(
    () => setSuggestions(makeEmptySuggestions()),
    []
  );

  // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
  const isMediumScreen = useMedia('(min-width: 672px)', true);
  useEffect(() => {
    setContentType(props.contentType);
  }, [props.contentType]);

  const handleDropdownChange = (contentType) => {
    setContentType(contentType);
  };
  const handleSubmit = (
    contentTypeSearched = contentType,
    value = searchTerm,
    searchOriginOverride = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    const searchOrigin = searchOriginOverride || searchOriginFromProps;
    onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
  };

  return (
    <NavSearchContextProvider {...props}>
      <NavSearchContext.Consumer>
        {({ setShowSuggestions }) => (
          <div
            className="flex flex-col xl:w-full lg:w-72 font-inter"
            data-cy="navSearch"
          >
            <form
              className="w-full h-10 px-4 py-2 bg-white rounded-3xl border border-solid border-gray-500 justify-between items-center inline-flex"
              onSubmit={(event) => {
                event.preventDefault();
                setShowSuggestions(false);
                handleSubmit(contentType, searchTerm);
              }}
            >
              <div className="w-full h-6 justify-start items-center gap-0.5 inline-flex">
                <NavSearchDropdown
                  onChange={handleDropdownChange}
                  value={contentType}
                  useSnapshots={useSnapshots}
                />
                <div className="xl:w-px lg:w-px md:w-px h-6 bg-gray-500" />
                {isMediumScreen ? (
                  <CrossAssetNavSearchBox
                    contentType={contentType}
                    setSearchTerm={setSearchTerm}
                    onSubmit={handleSubmit}
                    searchTerm={searchTerm}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    clearSuggestions={clearSuggestions}
                  />
                ) : (
                  <NavSearchBox
                    contentType={contentType}
                    onChange={setSearchTerm}
                    onSubmit={handleSubmit}
                    searchTerm={searchTerm}
                    userHasAnySubscription={userHasAnySubscription}
                  />
                )}
              </div>
              <NavSearchButton />
            </form>
          </div>
        )}
      </NavSearchContext.Consumer>
    </NavSearchContextProvider>
  );
}
